import { DateUtils } from '@/shared/utils/date-utils';

/**
 * Severidade da data de validade de uma solicitação de transferência.
 *
 * <p>Fonte única para as quatro telas da Central. A mesma regra estava copiada em
 * `abertas-uf-origem`, `abertas-uf-destino` e `localizar-por-cpf`, mais uma quarta variante
 * booleana em `aguardando-encerramento` — e é ela que pinta o selo de validade E alimenta o
 * indicador "Validade Vencida". Com quatro cópias, mudar o critério em uma tela e esquecer as
 * outras produziria contadores que discordam entre abas da mesma central.</p>
 */
export type SeveridadeValidade = 'success' | 'danger' | 'secondary';

/**
 * Devolve a severidade do selo de validade.
 *
 * - `danger` — venceu (anterior a hoje).
 * - `success` — ainda vale. A comparação é estrita, então vencer HOJE ainda conta como válida.
 * - `secondary` — data ausente ou ilegível: não dá para afirmar nem uma coisa nem outra, e pintar
 *   de verde ou vermelho seria inventar um estado.
 */
export function severidadeDataValidade(valor?: string | null): SeveridadeValidade {
    const data = (valor ?? '').trim();
    if (!DateUtils.isDateString(data)) {
        return 'secondary';
    }

    // Aceita `aaaa-mm-dd` e `dd/mm/aaaa`: as duas formas chegam do portal conforme o endpoint.
    const dataNormalizada = data.substring(0, 10).replace(/\//g, '-');
    const partes = dataNormalizada.split('-');

    let ano: number;
    let mes: number;
    let dia: number;

    if (partes[0].length === 4) {
        ano = Number(partes[0]);
        mes = Number(partes[1]);
        dia = Number(partes[2]);
    } else {
        dia = Number(partes[0]);
        mes = Number(partes[1]);
        ano = Number(partes[2]);
    }

    const dataValidade = new Date(ano, mes - 1, dia);
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    return dataValidade < hoje ? 'danger' : 'success';
}

/** Atalho de leitura para os contadores: só "venceu", sem passar pela severidade. */
export function validadeVencida(valor?: string | null): boolean {
    return severidadeDataValidade(valor) === 'danger';
}
